import tinycolor from 'tinycolor2';

export const VIEWS_TYPES = {
  HEX: 'hex',
  HSL: 'hsl',
  RGB: 'rgb',
};

export const DEFAULT_INITIAL_COLORS = [
  '#e62525',
  '#e48c33',
  '#e1aa41',
  '#f1f159',
  '#61d23f',
  '#5dc79e',
  '#69ddde',
  '#55b8ef',
  '#2e54ef',
  '#7414f5',
  '#af24f6',
  '#d730ac',
  '#000000',
  '#ffffff',
];

export const validatedParsedHsl = (input) => {
  const newHsl = parseUserHslInput(convertColorObj(input, Number));
  return isValidHsl(newHsl) ? { ...newHsl } : null;
};

/**
 * compare raw hsl input with tinycolor validated hsl after rounding
 * (input from react-colors is floated)
 * @param input
 * @returns boolean
 */
export const isValidHsl = (input) => {
  return checkHslValues(convertColorObj(tinycolor(input).toHsl(), roundDecimal), convertColorObj(input, roundDecimal));
};

export const isValidHex = (hex, supportAlpha = false) => {
  const lh = String(hex).charAt(0) === '#' ? 1 : 0;
  const tiny = tinycolor(hex);
  return (
    tiny.getFormat() &&
    tiny.getFormat().includes('hex') &&
    hex &&
    (hex.length === 3 + lh || hex.length === 6 + lh || (supportAlpha && hex.length === 8 + lh)) &&
    tiny.isValid()
  );
};

export const validatedHex = (hex) => {
  return isValidHex(hex) && { hex: hex };
};

/**
 * react-colors saves hsl as decimals - so we need to format and reformat
 * @param hsl {{a: number, s: number, h: number, l: number}}
 * @returns {{a: number, s: number, h: number, l: number}}
 */
export const reformatSyncedHslValues = (hsl) => {
  return {
    h: Math.round(hsl.h),
    s: Math.round(hsl.s * 100),
    l: Math.round(hsl.l * 100),
    a: hsl.a,
  };
};

export const parseUserHslInput = (hsl) => {
  return { ...hsl, ...{ s: hsl.s / 100, l: hsl.l / 100 } };
};

export const roundDecimal = (input) => {
  return Math.round(input * 100) / 100;
};

/**
 * since tinycolor is converting hsl to rgb and rgb does not represent hue value directly
 * we need to compare only saturation, lightness and alpha.
 * keeping hue value for better UX (show hue value always) also when
 * edge case for example - white/black color with hue.
 * @param hsl1 {{a: number, s: number, h: number, l: number}}
 * @param hsl2 {{a: number, s: number, h: number, l: number}}
 * @returns {null|boolean}
 */
export const checkHslValues = (hsl1, hsl2) => {
  return hsl1.s === hsl2.s && hsl1.l === hsl2.l && hsl1.a === hsl2.a;
};

export const convertColorObj = (colorObj, conversionFn) => {
  return Object.keys(colorObj).reduce((acc, key) => {
    acc[key] = conversionFn(colorObj[key]);
    return acc;
  }, {});
};

export const convertToColorObj = (hex) => {
  const color = tinycolor(hex);
  const rgba = color.toRgb();
  const roundedAlpha = roundDecimal(rgba.a);
  return {
    hex: color.toHexString(),
    rgb: { ...rgba, a: roundedAlpha },
    hsl: { ...color.toHsl(), a: roundedAlpha },
    hsv: { ...color.toHsv(), a: roundedAlpha },
  };
};

export const isColorExist = (color, colors) => {
  return colors.includes(color);
};

/**
 * get hex value
 * @param color
 * @returns {string}
 */
export const getHexValueByAlpha = (color) => {
  const tinyColorObj = tinycolor(color);
  return color.a !== 1 ? tinyColorObj.toHex8String() : tinyColorObj.toHexString();
};

/**
 * parse hex input value
 * @param color
 * @returns {string}
 */
export const parseHexInputValueByAlpha = (color) => {
  const tinyColorObj = tinycolor(color);
  return tinyColorObj.getAlpha() !== 1 ? tinyColorObj.toHex8String() : tinyColorObj.toHexString();
};

/**
 * validate colors list - no dups and max 14 predefined colors
 * @param colors
 * @returns {any[]}
 */
export const validateColorsList = (colors) => {
  return [...new Set(colors)].slice(0, 14);
};
